import type { ConnectorFolder, ConnectorItem, ConnectorSync, KnowledgeBase } from '@ralph/contracts'
import { SURFACE_LABEL } from '@ralph/contracts'
import type { I18n, Messages } from '@/i18n/index.ts'
import type { FolderReaders, FoldersFmt } from '@/i18n/messages/en/knowledgeFolders.ts'

/**
 * The pure half of the connected-folders surfaces (PRD-25).
 *
 * Everything here answers "given this folder, what is true and what should the row say", and
 * returns a state or a sentence — never a component. It is the half worth pinning with tests,
 * because each function exists to stop the screen telling a specific lie: a paused folder
 * painted as a fault, a throttled one painted red, a healthy one saying nothing at all.
 *
 * Anything that prints a number, a size or an elapsed time — or a word — takes `i18n` as its
 * first argument. This module is React-free by design, which is what makes it testable, so the
 * active locale's WORDS and formatters both arrive from the component that has `useI18n()`
 * rather than being imported bound here, where they would freeze at module load and then
 * render the previous language with no error of any kind (PRD-27 F-3, LOC-020).
 *
 * That is also why this module exists at all rather than living in the catalogue: everything
 * here CONSUMES the messages, and consuming logic stays outside the messages tree (LOC-017,
 * D-25). `FILE_TICK_LIMIT` below is the plainest case — a tick limit that could differ between
 * English and Portuguese is a bug, not a translation.
 */

/** Re-exported so a consumer needs one import for the shape and the functions that build it. */
export type { FolderReaders }

/**
 * The words, narrowed to the one namespace these functions read.
 *
 * `Pick<Messages, 'knowledgeFolders'>` rather than the whole `Messages` so that a signature
 * says which catalogue it depends on, and so a test can hand one of these functions a single
 * namespace instead of building a whole product's worth of copy. `useI18n()` satisfies it, so
 * a component passes its `useI18n()` result unchanged.
 */
export type FolderWords = { t: Pick<Messages, 'knowledgeFolders'> }

/** What a function here needs from the active locale: the words, and the formatters they take. */
export type FolderI18n = FoldersFmt & FolderWords

/** …plus the three formatters a row composes for itself, around the catalogue's sentences. */
export type FolderFmt = FolderI18n & Pick<I18n, 'formatBytes' | 'formatAgo' | 'formatUntil'>

// ---------------------------------------------------------------------------
// Standing
// ---------------------------------------------------------------------------

export type FolderStanding =
  | 'watching'
  | 'loading'
  | 'counting'
  | 'not_started'
  | 'paused'
  | 'needs_sign_in'
  | 'failed'
  | 'slowed'
  | 'stopped'

/** What a folder is doing, in the eight words a row can carry. Four of them are not faults. */
export const standingOf = (folder: ConnectorFolder): FolderStanding => {
  if (folder.status === 'previewing') {
    if (folder.syncing || folder.lastSyncAt === null) return 'counting'
    // A count that failed is not an empty folder waiting to be started; it is a fault with a remedy.
    if (folder.lastError) return 'stopped'
    return 'not_started'
  }
  if (folder.status === 'needs_reauth' || folder.connection.health === 'needs_reauth') return 'needs_sign_in'
  if (folder.status === 'error') return 'stopped'
  if (folder.status === 'paused') return 'paused'
  if (folder.connection.health === 'rate_limited') return 'slowed'
  if (folder.stats.queued > 0 || folder.syncing) return 'loading'
  if (folder.lastError && folder.lastSuccessAt === null) return 'failed'
  if (folder.lastError && folder.lastSyncAt && folder.lastSuccessAt && folder.lastSyncAt > folder.lastSuccessAt)
    return 'failed'
  return 'watching'
}

/** Only the person who signed in with a delegated account, or an administrator, can renew it (PRD-25 D-3). */
export const canRenew = (
  connection: { mode: string; ownerId: string },
  viewer: { id: string | null; admin: boolean }
): boolean => connection.mode === 'delegated' && (viewer.admin || connection.ownerId === viewer.id)

/**
 * Whether this viewer may read THROUGH an account (PRD-26 D-38): their own sign-in, or — for an
 * administrator — any account, the company ones included. Every folder operation that reads
 * through the folder's account (check, start, change what is included, put back, resume, retry,
 * fetch again, a different account) is refused to anybody else, so it is not offered to them.
 * Pausing and disconnecting only ever read less, and stay open to every maintainer.
 */
export const mayUseConnection = (
  connection: { mode: string; ownerId: string },
  viewer: { id: string | null; admin: boolean }
): boolean => viewer.admin || (connection.mode === 'delegated' && connection.ownerId === viewer.id)

/** "next in 4m", or, once the moment has passed and the sweep has not yet claimed it, "any moment now" — never "expired". */
export const dueIn = (i18n: FolderWords & Pick<I18n, 'formatUntil'>, iso: string): string =>
  new Date(iso).getTime() <= Date.now() ? i18n.t.knowledgeFolders.standing.dueNow : i18n.formatUntil(iso)

/** Standings that need a person, as opposed to time. */
export const needsPerson = (standing: FolderStanding): boolean =>
  standing === 'needs_sign_in' || standing === 'failed' || standing === 'stopped'

/** The word on the row. */
export const standingLabel = (i18n: FolderI18n, folder: ConnectorFolder, standing = standingOf(folder)): string => {
  const t = i18n.t.knowledgeFolders
  const surface = SURFACE_LABEL[folder.surface]
  switch (standing) {
    case 'watching':
      return t.standing.watching
    case 'loading':
      return t.standing.loading(i18n, folder.stats.synced, folder.stats.included)
    case 'counting':
      return t.standing.counting
    case 'not_started':
      return t.standing.notStarted
    case 'paused':
      return t.standing.paused
    case 'needs_sign_in':
      return t.standing.needsSignIn
    case 'failed':
      return t.standing.failed
    case 'slowed':
      return t.standing.slowed(surface)
    case 'stopped':
      return t.standing.failed
  }
}

/** The four facts a maintainer scans a row for. */
export const folderLine = (i18n: FolderFmt, folder: ConnectorFolder, standing = standingOf(folder)): string => {
  const t = i18n.t.knowledgeFolders
  const parts: string[] = []
  if (standing === 'counting') {
    parts.push(t.standing.countingSoFar(i18n, folder.stats.files))
    return parts.join(' · ')
  }
  if (standing === 'not_started') return t.standing.notStartedHint
  // Paused says what paused means: nothing new, nothing lost (D-18).
  if (standing === 'paused') return t.standing.pausedHint
  parts.push(t.standing.included(i18n, folder.stats.included, folder.stats.files))
  if (folder.stats.includedBytes > 0) parts.push(i18n.formatBytes(folder.stats.includedBytes))
  if (folder.lastSuccessAt) parts.push(t.standing.checked(i18n.formatAgo(folder.lastSuccessAt)))
  if (standing === 'watching' && folder.nextSyncAt) parts.push(t.standing.next(dueIn(i18n, folder.nextSyncAt)))
  return parts.join(' · ')
}

/** The second line when something needs a person: what happened, and what did not. */
export const problemSentence = (i18n: FolderFmt, folder: ConnectorFolder): { title: string; body: string } | null => {
  const t = i18n.t.knowledgeFolders
  const standing = standingOf(folder)
  const surface = SURFACE_LABEL[folder.surface]
  const name = folder.displayPath.split(' / ').pop() ?? folder.displayPath
  const docs = folder.stats.synced
  if (standing === 'needs_sign_in') {
    const detail = folder.connection.healthDetail ?? folder.lastError
    if (detail?.code === 'owner_deactivated' || folder.lastError?.code === 'owner_deactivated') {
      return { title: t.standing.ownerLeft(folder.connection.ownerName), body: t.standing.ownerLeftBody() }
    }
    return {
      title: t.standing.needsSignIn,
      body: t.standing.needsSignInHint(folder.lastSuccessAt ? i18n.formatAgo(folder.lastSuccessAt) : null)
    }
  }
  if (standing === 'slowed') {
    return {
      title: t.standing.slowed(surface),
      body: t.standing.slowedHint(folder.connection.retryAfter ? dueIn(i18n, folder.connection.retryAfter) : null)
    }
  }
  if (standing === 'stopped' || standing === 'failed') {
    const code = folder.lastError?.code ?? ''
    if (code.startsWith('not_found'))
      return { title: t.standing.rootGone(surface, name), body: t.standing.rootGoneBody(i18n, docs) }
    if (code.startsWith('forbidden')) {
      return {
        title: t.standing.forbidden(name),
        body: t.standing.forbiddenBody(i18n, docs, folder.connection.accountLabel)
      }
    }
    // Keyed off the code like every branch around it (PRD-27 D-6). This one used to read
    // `message.includes('came back empty')` — choosing which prose to show by matching other
    // prose, which breaks the moment the server rewords its sentence and breaks permanently
    // once that sentence is no longer English.
    if (code === 'empty_listing') {
      return { title: t.standing.emptyListing(name), body: t.standing.emptyListingBody }
    }
    if (code === 'sync_interrupted') return { title: t.standing.failed, body: t.standing.interrupted }
    // The last resort is the STORED sentence, and it stays English: a `ConnectorProblem` carries
    // `code`, `message` and `at` — no `messageKey`, no `params` — so there is nothing for the
    // catalogue to look up, and `describeApiError` has no refusal to describe. Every branch above
    // exists so this line is the rare case rather than the rule; keying it needs the sync writer
    // to record a key beside the prose it already records (PRD-27 D-4).
    return { title: t.standing.failed, body: folder.lastError?.message ?? '' }
  }
  if (folder.status === 'paused' && folder.lastError?.code === 'owner_deactivated') {
    return { title: t.standing.ownerLeft(folder.connection.ownerName), body: t.standing.ownerLeftBody() }
  }
  return null
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

/** One line of history: what a check did, in words, with "nothing had changed" as a result. */
export const syncLine = (i18n: FolderI18n, sync: ConnectorSync): string => {
  const t = i18n.t.knowledgeFolders
  if (sync.status === 'running') return t.detail.checkingRow
  if (sync.status === 'failed') {
    const code = sync.error?.code ?? ''
    if (code.startsWith('auth')) return t.detail.stoppedSignIn
    // Same stored-prose fallback, for the same reason as `problemSentence` above.
    return t.detail.stopped(sync.error?.message ?? t.detail.stoppedUnknown)
  }
  if (sync.kind === 'preview') return t.detail.counted(i18n, sync.listed)
  // The tally is the catalogue's, shared with the sentence the footer prints after a check:
  // "added", "changed" and "withdrawn" are words, and a word assembled here would be a word
  // no translator ever sees (LOC-013).
  const marks = t.detail.syncTally(i18n, sync.added, sync.changed, sync.removed, sync.withdrawn)
  return marks === '' ? t.detail.nothingChanged : marks
}

// ---------------------------------------------------------------------------
// The knowledge base the folder loads into
// ---------------------------------------------------------------------------

/** True when the base has a folder a person must act on. */
export const foldersNeedPerson = (base: Pick<KnowledgeBase, 'connectedFolders'>): boolean =>
  base.connectedFolders.needsPerson > 0

/**
 * Who will be able to search what a folder loads: exactly who can use the base (PRD-25 D-8),
 * named the way the base's own "Who can use it" names them — "you", "Finance EMEA's managers",
 * the groups it is shared with, or everyone who signs in. `people` is the distinct count from
 * the base's access view when it has arrived.
 */
export const folderReaders = (
  i18n: FolderWords,
  base: Pick<KnowledgeBase, 'access' | 'owner'>,
  viewerId: string | null,
  people: number | null = null
): FolderReaders => {
  const t = i18n.t.knowledgeFolders
  if (base.access.kind === 'everyone') return { kind: 'everyone' }
  if (base.access.kind === 'groups' && base.access.groups.length > 0) {
    return { kind: 'groups', names: base.access.groups.map((group) => group.name), people }
  }
  const who =
    base.owner.kind === 'group'
      ? t.access.groupManagers(base.owner.group.name)
      : base.owner.id === viewerId
        ? t.access.you
        : base.owner.name
  return { kind: 'private', who }
}

/**
 * The sentence a maintainer confirms before a folder starts or keeps syncing — sent back
 * verbatim, so the audit trail records what they were shown. An agent allowed to answer from
 * the base for everyone it reaches widens the audience too (D-16), so it is part of the claim.
 */
export const folderAckText = (
  i18n: FolderI18n,
  base: Pick<KnowledgeBase, 'access' | 'owner' | 'name' | 'lentToCount'>,
  viewerId: string | null
): string => {
  const t = i18n.t.knowledgeFolders
  const readers = folderReaders(i18n, base, viewerId)
  const headline = t.access.headline(readers, base.name)
  // Shared with everyone already reaches everyone an agent could; the agents add nobody.
  return base.lentToCount > 0 && readers.kind !== 'everyone'
    ? `${headline} ${t.access.agents(i18n, base.lentToCount, base.name)}`
    : headline
}

/** A folder must be acknowledged before it starts when it has never been, or when its base widened since. */
export const needsAcknowledgement = (folder: Pick<ConnectorFolder, 'audience'>): boolean =>
  !folder.audience.acknowledged || folder.audience.stale

// ---------------------------------------------------------------------------
// Selection table helpers
// ---------------------------------------------------------------------------

/** What a folder row says under its name: the ratio is the state, the glyph confirms it. */
export const folderRowDetail = (i18n: FolderI18n, item: ConnectorItem): string => {
  const t = i18n.t.knowledgeFolders
  const { files, included } = item.subtree
  if (files === 0) return item.effective === 'none' ? t.selection.leftOutFolder : t.selection.keptUpToDate
  if (item.effective === 'none') return t.selection.leftOutFolder
  if (included === 0) return t.selection.allFilteredOut(i18n, files)
  if (item.effective === 'some' || included < files) return t.selection.someOf(i18n, included, files)
  return t.selection.keptUpToDate
}

/** Files are ticked one by one only when a level is small enough to read. */
export const FILE_TICK_LIMIT = 500

/** Below this many files, the file list at a level is shown; above it, one summary line. */
export const filesAreTickable = (filesAtLevel: number): boolean => filesAtLevel < FILE_TICK_LIMIT

// ---------------------------------------------------------------------------
// Sizes and dates as the copy wants them
// ---------------------------------------------------------------------------

// `mb()` lived here as a second name for `formatBytes`. It had no call sites left, and a
// second byte formatter is exactly what LOC-020 exists to prevent, so it is deleted rather
// than threaded through (D-27: dead strings and dead helpers go, they do not get localized).
//
// `surfaceOf(provider, surface)` came out of the catalogue on the same terms and went the same
// way. It chose between `SURFACE_LABEL` and `PROVIDER_LABEL` — two proper-noun maps, neither
// of them translatable — and had no caller anywhere; the three screens that need that choice
// each make it inline from the field they already hold.

export const yearOf = (iso: string): number => new Date(iso).getUTCFullYear()

/** Left-truncation for a path: the tail carries the information, the head repeats per row. */
export const tailOfPath = (path: string, max = 48): string =>
  path.length <= max ? path : `…${path.slice(path.length - max + 1)}`

/** A folder paused because the person who signed in left: the remedy is somebody else's sign-in. */
export const ownerLeft = (folder: ConnectorFolder): boolean =>
  folder.status === 'paused' && folder.lastError?.code === 'owner_deactivated'
